import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

// Set to true after Google OAuth has been configured in both
// the Supabase dashboard and the Google Cloud Console.
const GOOGLE_LOGIN_ENABLED = false;

// Where the course bundle sends people after a successful Stripe checkout.
const COURSE_URL = 'https://voice-training.tedsvoiceacademy.com/courses/master-your-pass-profile/';

function LoginPage() {
  // Stripe sends course-bundle buyers back here with ?purchase=course_bundle so we can
  // confirm the sale and point them at the course, which lives on another domain.
  const justBoughtCourseBundle =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('purchase') === 'course_bundle';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // A buyer arriving straight from checkout needs to create an account, not log in.
  const [mode, setMode] = useState(justBoughtCourseBundle ? 'signup' : 'login'); // login | signup | coupon
  const [agreed, setAgreed] = useState(false);
  // If the visitor already has a Supabase session, hold their identity here
  // and show a Continue/Switch-account banner instead of auto-redirecting.
  // This is the user-controlled escape hatch from Chrome auto-logging into
  // the wrong account.
  const [existingSession, setExistingSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  // Route user to the right place based on their status
  const routeUser = async (userId) => {
    // Fetch user profile (admin status, payment, report)
    const { data: userRow, error: userError } = await supabase
      .from('users')
      .select('is_admin, payment_status, report_status')
      .eq('id', userId)
      .single();

    if (userError) {
      console.error('routeUser: Error fetching user profile:', userError);
    }

    // Admin → admin dashboard
    if (userRow && userRow.is_admin === true) {
      window.location.href = '/admin';
      return;
    }

    // Check payment status — must be paid, free_admin, or coupon
    const hasAccess = userRow && (
      userRow.payment_status === 'paid' ||
      userRow.payment_status === 'free_admin' ||
      userRow.payment_status === 'coupon'
    );

    if (!hasAccess) {
      // No payment on record — sign out and show error
      await supabase.auth.signOut();
      setError('No active subscription found. Please purchase access at tedsvoiceacademy.com or use a coupon code.');
      setLoading(false);
      return;
    }

    // If report is ready → portal
    if (userRow.report_status === 'completed') {
      window.location.href = '/results';
      return;
    }

    // Check if user has a completed assessment
    const { data: completedAssessment } = await supabase
      .from('assessments')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .limit(1)
      .single();

    if (completedAssessment) {
      // Completed assessment — go to portal (report pending or generating)
      window.location.href = '/results';
      return;
    }

    // Has access — go to assessment
    window.location.href = '/assessment';
  };

  // Run after any sign-in (password OR OAuth) to claim prior Stripe payments
  // before routing.
  const verifyPaymentAndRoute = async (userId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetch('/.netlify/functions/verify-payment', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
      }
    } catch (e) {
      console.error('verify-payment call failed:', e);
    }
    await routeUser(userId);
  };

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // DON'T auto-redirect. Instead, surface a banner so the visitor can
      // either continue into their account OR sign out and use a different
      // one. This is what gives Ted (and any user) a way around Chrome's
      // session persistence.
      setExistingSession({ id: user.id, email: user.email });
    }
    setCheckingSession(false);
  };

  const handleContinueAsExisting = async () => {
    if (!existingSession) return;
    setLoading(true);
    // Run verify-payment so any prior Stripe purchase claims this account, then route.
    await verifyPaymentAndRoute(existingSession.id);
  };

  const handleSwitchAccount = async () => {
    setLoading(true);
    setError('');
    await supabase.auth.signOut();
    setExistingSession(null);
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    if (!agreed) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    setError('');

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        // Forces Google to show the account chooser EVERY time, so Chrome
        // can't silently pick the most recently used Google account.
        queryParams: { prompt: 'select_account' },
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      setLoading(false);
    }
    // On success, the browser is redirected to Google. No further code runs here.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreed) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    setError('');

    if (mode === 'coupon') {
      try {
        const response = await fetch('/.netlify/functions/redeem-coupon', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: couponCode,
            email,
            password,
            fullName,
          }),
        });

        const data = await response.json();
        if (response.ok && data.success) {
          // Log them in automatically
          const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
          if (signInError) {
            setError('Account created! Please log in with your credentials.');
            setMode('login');
          } else {
            window.location.href = '/assessment';
          }
        } else {
          setError(data.error || 'Failed to redeem coupon');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      }
      setLoading(false);
      return;
    }

    if (mode === 'login') {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        await verifyPaymentAndRoute(data.user.id);
      }
    } else {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        await verifyPaymentAndRoute(data.user.id);
      }
    }
  };

  // If we're still checking, render a small placeholder so the page doesn't
  // flash either UI prematurely.
  if (checkingSession) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>PASS Profile 3.0</h1>
            <p style={{ color: '#6B7A8F' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // When an existing session is detected, show the choice banner INSTEAD of the form.
  // The user can either continue into their account or sign out and use a different one.
  if (existingSession) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>PASS Profile 3.0</h1>
            <p>You're already signed in</p>
          </div>

          <div style={bannerStyles.card}>
            <p style={bannerStyles.label}>Signed in as</p>
            <p style={bannerStyles.email}>{existingSession.email}</p>

            <button
              type="button"
              onClick={handleContinueAsExisting}
              disabled={loading}
              style={{ ...bannerStyles.primaryBtn, opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Please wait...' : 'Continue to my account →'}
            </button>

            <button
              type="button"
              onClick={handleSwitchAccount}
              disabled={loading}
              style={{ ...bannerStyles.secondaryBtn, opacity: loading ? 0.6 : 1 }}
            >
              Sign out and use a different account
            </button>
          </div>

          <div className="back-home">
            <a href="/">← Back to home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>PASS Profile 3.0</h1>
          <p>{mode === 'login' ? 'Welcome back' : mode === 'coupon' ? 'Redeem your coupon code' : 'Create your account'}</p>
        </div>

        {justBoughtCourseBundle && (
          <div style={purchaseBannerStyle}>
            <h2 style={purchaseBannerTitleStyle}>Thank you. Your purchase is complete.</h2>
            <p style={purchaseBannerTextStyle}>
              <strong>Your assessment:</strong> create your account below using the same
              email address you just paid with, and you can start right away.
            </p>
            <p style={purchaseBannerTextStyle}>
              <strong>Your course:</strong> Master Your PASS Profile is ready now at{' '}
              <a href={COURSE_URL} target="_blank" rel="noopener noreferrer" style={purchaseBannerLinkStyle}>
                the course page
              </a>
              . Click "Already purchased?" and enter that same email address. Your progress
              saves automatically and access never expires.
            </p>
            <p style={purchaseBannerNoteStyle}>
              We've emailed you these details too, in case you want them later.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          {/* Google sign-in — hidden until Supabase + Google Cloud OAuth setup is complete.
              Flip GOOGLE_LOGIN_ENABLED to true to enable. */}
          {GOOGLE_LOGIN_ENABLED && mode !== 'coupon' && (
            <>
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading || !agreed}
                style={googleBtnStyle(loading || !agreed)}
              >
                <span style={googleGStyle}>G</span>
                <span>Sign in with Google</span>
              </button>
              <div style={dividerStyle}>
                <span style={dividerLineStyle}></span>
                <span style={dividerTextStyle}>or use email</span>
                <span style={dividerLineStyle}></span>
              </div>
            </>
          )}

          {mode === 'coupon' && (
            <>
              <div className="form-group">
                <label htmlFor="couponCode">Coupon Code</label>
                <input
                  id="couponCode"
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  required
                  disabled={loading}
                  placeholder="e.g. PASS-FRIEND"
                  style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, letterSpacing: 1 }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Your full name"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              placeholder="••••••••"
              minLength={6}
            />
          </div>

          <div className="form-group consent-group">
            <label className="consent-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={loading}
              />
              <span>
                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </span>
            </label>
          </div>

          <button type="submit" className="submit-button" disabled={loading || !agreed}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : mode === 'coupon' ? 'Redeem & Create Account' : 'Sign Up'}
          </button>
        </form>

        <div className="login-footer">
          {mode === 'login' && (
            <p>
              Don't have an account?{' '}
              <button onClick={() => { setMode('signup'); setError(''); }} className="link-button">
                Sign up
              </button>
            </p>
          )}
          {(mode === 'signup' || mode === 'coupon') && (
            <p>
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(''); }} className="link-button">
                Log in
              </button>
            </p>
          )}

          {/* Prominent coupon path — shown on BOTH the Log In and Sign Up screens
              so coupon holders never miss it. Previously this was a small text
              link on the Log In screen only, which people missed (Session 27). */}
          {mode !== 'coupon' && (
            <div style={couponCalloutStyle}>
              <span style={couponCalloutTextStyle}>Have a coupon code?</span>
              <button
                type="button"
                onClick={() => { setMode('coupon'); setError(''); }}
                style={couponBtnStyle}
              >
                Redeem it here →
              </button>
            </div>
          )}
        </div>

        <div className="back-home">
          <a href="/">← Back to home</a>
        </div>
      </div>
    </div>
  );
}

// Inline styles for the prominent coupon callout (kept here so we don't touch
// the global login CSS file). Shown on both the Log In and Sign Up screens.
const couponCalloutStyle = {
  marginTop: 18,
  padding: 16,
  borderRadius: 10,
  border: '1px solid #B7E6ED',
  backgroundColor: '#F0FBFD',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
};

const couponCalloutTextStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: '#0F2E3A',
};

const couponBtnStyle = {
  display: 'inline-block',
  padding: '10px 22px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#2BB3C9',
  color: 'white',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// Inline styles for the Google button + divider (kept here so we don't touch
// the global login CSS file).
const googleBtnStyle = (disabled) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  width: '100%',
  padding: '12px 16px',
  borderRadius: 8,
  border: '1px solid #D0D5DD',
  backgroundColor: 'white',
  color: '#1F1F1F',
  fontSize: 15,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  marginBottom: 16,
  fontFamily: 'inherit',
});

const googleGStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: 'conic-gradient(from -45deg, #4285F4 0deg 90deg, #34A853 90deg 180deg, #FBBC05 180deg 270deg, #EA4335 270deg 360deg)',
  color: 'white',
  fontSize: 13,
  fontWeight: 700,
  fontFamily: 'Arial, sans-serif',
};

const dividerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '8px 0 16px 0',
};

const dividerLineStyle = {
  flex: 1,
  height: 1,
  background: '#E0E4EA',
};

const dividerTextStyle = {
  fontSize: 12,
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

// Post-purchase confirmation banner (course bundle buyers arriving from Stripe).
const purchaseBannerStyle = {
  background: '#F4F8F7',
  border: '1px solid #2A9D8F',
  borderRadius: 8,
  padding: '20px 22px',
  marginBottom: 24,
  textAlign: 'left',
};

const purchaseBannerTitleStyle = {
  fontSize: 18,
  color: '#0B3D2E',
  margin: '0 0 12px 0',
};

const purchaseBannerTextStyle = {
  fontSize: 14,
  lineHeight: 1.6,
  color: '#333',
  margin: '0 0 12px 0',
};

const purchaseBannerLinkStyle = {
  color: '#2A9D8F',
  fontWeight: 600,
};

const purchaseBannerNoteStyle = {
  fontSize: 13,
  color: '#6B7A8F',
  margin: 0,
};

// Existing-session banner styles.
const bannerStyles = {
  card: {
    background: 'white',
    border: '1px solid #E0E4EA',
    borderRadius: 10,
    padding: '24px 20px',
    textAlign: 'center',
    marginBottom: 16,
  },
  label: {
    fontSize: 12,
    color: '#6B7A8F',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
    margin: '0 0 6px 0',
  },
  email: {
    fontSize: 16,
    fontWeight: 600,
    color: '#0B3D2E',
    margin: '0 0 20px 0',
    wordBreak: 'break-all',
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#2A9D8F',
    color: 'white',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 10,
    fontFamily: 'inherit',
  },
  secondaryBtn: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #D0D5DD',
    background: 'white',
    color: '#1F1F1F',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default LoginPage;
